//This is the API for the current user's account

import { User } from '../auth/User';
import { GlobalUser } from '../types/GlobalUser';
import { ManageCompanyUserRequest } from '../types/UserMembership';
import { ApiRoutes, getFullApiRoute, replaceRouteParams } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';

export class UserAPI {
  /**
   *
   * @param user - Current authenticated user
   * @returns Array of GlobalUser objects
   */
  static async getAll(user: User): Promise<GlobalUser[]> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.USERS),
      'GET',
      null,
      new Headers(),
      user,
    );
    return result?.data || [];
  }

  /**
   *
   * @param user - Current authenticated user
   * @param companyUserId - Company user id
   * @param companyId - Optional company ID for account admin users
   * @returns User Object
   */
  static async getOne(user: User, companyUserId: string, companyId?: string): Promise<object> {
    let url = getFullApiRoute(ApiRoutes.USERS_V3) + `/${companyUserId}`;

    // Append companyId as query parameter if provided
    if (companyId) {
      const urlParams = new URLSearchParams();
      urlParams.append('companyId', companyId);
      url += '?' + urlParams.toString();
    }

    const result = await HttpRequest(url, 'GET', null, null, user);
    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result && result.data ? (Array.isArray(result.data) ? result.data[0] : result.data) : {};
  }

  /**
   *
   * @param user - Current authenticated user
   * @param data - Data containing userId to be removed
   * @returns Boolean indicating success of the operation
   */
  static async removeUser(user: User, data: { userId: string }): Promise<boolean> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.REMOVE_USER), { id: data.userId }),
      'DELETE',
      null,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    //Check if we have a result and if it is object or array, if array that is what we expect
    return result && result.data && Array.isArray(result.data);
  }

  /**
   *
   * @param user - Current authenticated user
   * @returns  Array of GlobalUser objects representing the current user
   */
  static async getCurrentUser(user: User): Promise<GlobalUser[]> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.CURRENT_USER_V3),
      'GET',
      null,
      new Headers(),
      user,
    );
    return result?.data || [];
  }

  /**
   *
   * @param user - Current authenticated user
   * @param data - Partial data to update the current user
   * @returns Array of updated GlobalUser objects
   */
  static async updateCurrentUser(user: User, data: Partial<GlobalUser>): Promise<GlobalUser[]> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.CURRENT_USER_UPDATE_V3),
      'PUT',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data || [];
  }

  /**
   *
   * @param user - Current authenticated user
   * @param data - Partial data to update the user details
   * @returns Array of updated GlobalUser objects
   */
  static async updateUserDetails(
    user: User,
    data: { companyId?: string; status: string; userId: string },
  ) {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.USER_DETAILS_UPDATE_V3), {
        userId: data.userId,
      }),
      'PATCH',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data || [];
  }

  /**
   *
   * @param user - Current authenticated user
   * @param data
   * @returns
   */
  static async updateUserMembership(
    user: User,
    data: { companyId: string; adminLevel: string; userId: string },
  ) {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.USER_MEMBERSHIPS_V3), {
        companyId: data.companyId,
        userId: data.userId,
      }),
      'PUT',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data || [];
  }

  /**
   *
   * @param user - Current authenticated user
   * @param data
   * @returns
   */
  static async deleteUserMembership(user: User, data: { companyId: string; userId: string }) {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.USER_MEMBERSHIPS_V3), {
        companyId: data.companyId,
        userId: data.userId,
      }),
      'DELETE',
      null,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data || [];
  }

  static async manageCompanies(
    user: User,
    data: ManageCompanyUserRequest,
  ): Promise<{ success: boolean; data?: unknown; error?: string }> {
    try {
      const requestBody = {
        companyMemberships: data.companyMemberships.map((company) => ({
          companyId: company.companyId,
          locationIds: company.locations,
          customRoleIds: company.customRoleIds ?? [],
        })),
      };

      const result = await HttpRequest(
        replaceRouteParams(getFullApiRoute(ApiRoutes.MANAGE_COMPANIES_V3), {
          userId: data.userId,
        }),
        'POST',
        requestBody,
        new Headers({ 'Content-Type': 'application/json' }),
        user,
      );

      return { success: true, data: result };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to invite user',
      };
    }
  }

  /**
   * Update user details including firstName, lastName, and adminLevel
   * Only System Admins can call this endpoint
   *
   * @param user - Current authenticated user
   * @param data - User update data
   * @returns Updated user data
   */
  static async updateUser(
    user: User,
    data: {
      userId: string;
      firstName?: string;
      lastName?: string;
      adminLevel?: string;
      customRoleIds?: string[];
    },
  ) {
    const { userId, ...updateData } = data;

    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.USER_UPDATE_V3), {
        userId,
      }),
      'PUT',
      updateData,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data || [];
  }
}
